import React, { useState } from 'react';
import useNavbarScroll from '../../hooks/useNavbarScroll';
import { Menu, X } from 'lucide-react';

export default function Navbar({ onSignIn, onGetStarted, landingTheme, onToggleTheme }) {
  const { scrolled } = useNavbarScroll();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <nav
      className={`sticky top-0 z-50 transition-all duration-300 ease-in-out w-full ${
        scrolled ? 'shadow-sm' : ''
      }`}
      style={{
        background: scrolled ? 'var(--lp-nav-bg)' : 'transparent',
        backdropFilter: scrolled ? 'blur(16px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(16px)' : 'none',
        borderBottom: scrolled ? '1px solid var(--lp-border)' : '1px solid transparent',
      }}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
        
        {/* Left group: Logo + Nav */}
        <div className="flex items-center gap-10">
          {/* Logo */}
          <a href="/" className="flex items-center">
            <img
              src={landingTheme === 'dark' ? '/resumebar-logo-dark.svg' : '/resumebar-logo-horizontal.svg'}
              alt="Resumebar Logo"
              className="h-8 cursor-pointer select-none"
            />
          </a>

          {/* Nav links */}
          <div className="hidden md:flex items-center gap-8">
            {[
              { label: 'About', href: '/about' },
              { label: 'How it works', href: '/#how-it-works' },
              { label: 'Reviews', href: '/#reviews' },
              { label: 'Plans', href: '/plans' },
              { label: 'Help', href: '/help' }
            ].map(link => (
              <a
                key={link.label}
                href={link.href}
                className="text-sm font-medium transition-colors duration-200"
                style={{ color: 'var(--lp-text-muted)' }}
                onMouseEnter={e => e.target.style.color = 'var(--lp-text)'}
                onMouseLeave={e => e.target.style.color = 'var(--lp-text-muted)'}
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>

        {/* Right group */}
        <div className="flex items-center gap-3">
          {/* Theme toggle */}
          <button
            onClick={onToggleTheme}
            className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 select-none text-lg"
            style={{
              border: '1px solid var(--lp-border)',
              color: 'var(--lp-text-muted)',
            }}
            aria-label={`Switch to ${landingTheme === 'dark' ? 'light' : 'dark'} mode`}
            title={`Switch to ${landingTheme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {landingTheme === 'dark' ? '☀' : '☽'}
          </button>

          {/* Auth Action Buttons */}
          <button
            onClick={onSignIn}
            className="hidden md:inline-flex items-center justify-center px-4 py-1.5 text-xs font-bold uppercase tracking-wider rounded-lg border transition-all duration-200 hover:opacity-80 active:scale-95"
            style={{
              borderColor: 'var(--lp-border)',
              color: 'var(--lp-text)',
              background: 'transparent',
              cursor: 'pointer'
            }}
          >
            Sign In
          </button>
          <button
            onClick={onGetStarted}
            className="hidden md:inline-flex items-center justify-center px-4 py-1.5 text-xs font-bold uppercase tracking-wider rounded-lg transition-all duration-200 hover:opacity-90 active:scale-95"
            style={{
              background: 'var(--lp-accent, #FCA311)',
              color: '#14213D',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Get Started
          </button>
          
          {/* Mobile Menu Toggle */}
          <button 
            className="md:hidden flex items-center justify-center w-9 h-9 rounded-md transition-colors"
            style={{ color: 'var(--lp-text)' }}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu Dropdown */}
      {isMenuOpen && (
        <div className="md:hidden absolute top-16 left-0 w-full shadow-lg border-t" style={{ background: 'var(--lp-bg)', borderColor: 'var(--lp-border)' }}>
          <div className="flex flex-col px-4 py-4 gap-4">
            {[
              { label: 'About', href: '/about' },
              { label: 'How it works', href: '/#how-it-works' },
              { label: 'Reviews', href: '/#reviews' },
              { label: 'Plans', href: '/plans' },
              { label: 'Help', href: '/help' }
            ].map(link => (
              <a
                key={link.label}
                href={link.href}
                className="text-sm font-medium py-2 border-b transition-colors"
                style={{ color: 'var(--lp-text)', borderColor: 'var(--lp-border)' }}
                onClick={() => setIsMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}

            {/* Mobile Auth Buttons */}
            <div className="flex flex-col gap-2 mt-2">
              <button
                onClick={() => { setIsMenuOpen(false); onSignIn(); }}
                className="w-full py-2.5 rounded-lg border text-sm font-bold uppercase tracking-wider text-center cursor-pointer"
                style={{ borderColor: 'var(--lp-border)', color: 'var(--lp-text)', background: 'transparent' }}
              >
                Sign In
              </button>
              <button
                onClick={() => { setIsMenuOpen(false); onGetStarted(); }}
                className="w-full py-2.5 rounded-lg text-sm font-bold uppercase tracking-wider text-center cursor-pointer"
                style={{ background: 'var(--lp-accent, #FCA311)', color: '#14213D', border: 'none' }}
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
